/**
 * positionRollup.js — tax lots into positions
 * ===========================================
 * A position is an instrument held at a venue. A tax lot is how the book
 * records one purchase, conversion, delivery or sale of it, so a holding has
 * as many lots as it has had events and listing them reports the same position
 * several times at the same price.
 *
 * Brian, 15 September 2026, on Nexgel Inc.'s short appearing five times on the
 * dashboard: *"It should just report the one aggregate position, unless its
 * held at another prime broker as well, then two positions."* And, on what
 * stays apart: *"if its two separate instruments, then yes, you can keep them
 * separate. same with DEVS, the promissory note, crypto note, etc etc"* — so
 * DevvStream's convertible note, its promissory note of 6 March 2026 and its
 * pre-funded warrant of 27 April 2026 are three rows although §0.10 makes all
 * three tranches of one deal. The grouping is by instrument, never by deal.
 *
 * This lives in one module because both the Dashboard's Fund Positions table
 * and the NAV / Performance page's Portfolio holdings read the same NAV
 * payload and had the same defect — §0.21: fixing the one reader that had the
 * bug is half a fix.
 */

export const number = value => value === null || value === undefined || value === "" || !Number.isFinite(Number(value)) ? null : Number(value);

// The USD figure the row was converted to, falling back to the local amount
// only where the two are the same thing.
export const usdValue = p => number(p.mv_usd) ?? (p.currency === "USD" ? number(p.market_value) : null);

const symbolKey = value => String(value || "").toUpperCase().replace(/\s+[A-Z]{2}\s+EQUITY$/, "").replace(/[\s.-]/g, "");

// Where the position is held, and the only thing that splits one instrument
// into two rows.
//
// `TaxLot.custodian` is what fills it — added 16 September 2026 on Brian's
// *"yes add the custodian field to the lots"* — and it is blank on a lot
// nobody has recorded one for, which leaves that instrument as a single row
// naming whatever venues the broker files report. A caller that can derive a
// venue from elsewhere passes its own reader to `rollUpPositions`.
export const venueOnRow = p => p.broker || p.custodian || "";

/**
 * The identity of a position: the instrument, its currency and its venue.
 *
 * `security_id` is what the NAV engine returns for exactly this. Grouping on
 * the symbol instead would split a position whose ticker changed on a
 * delisting — DEVS became DEVSF (§0.9) — and merge two instruments that share
 * one, as High Wire Networks' senior secured debenture and its Series F
 * preferred both read HWNI. The ticker and name are the fallback for a row
 * from a server that predates the field.
 */
export const groupKey = p => [
  p.security_id ?? `t:${symbolKey(p.ticker)}:${String(p.name || "").trim().toLowerCase()}`,
  p.currency || "", p.instrument_type || "", p.venue || "",
].join("|");

// Sums a column over the lots, and reports nothing rather than a partial
// total when one of them is missing it: a cost basis summed over four lots of
// five is a number that looks like the position's cost and is not.
const total = (rows, read) => rows.every(r => read(r) !== null) ? rows.reduce((t, r) => t + read(r), 0) : null;
const field = name => r => number(r[name]);
const distinct = (rows, read) => [...new Set(rows.map(read).filter(v => v !== null && v !== undefined && v !== ""))];

// What one share cost, weighted across the lots: their cost over their
// quantity. Brian, 20 October 2026: *"please add cost per share on the
// dashboard as well. Its hard to tell what our avg cost basis is on the SHFS
// short and NXGL short"*.
//
// **Only the lots on the row's own side are averaged.** A row can hold both:
// SHF Holdings' common is three short lots of 150,000 shares and one long lot
// of 454, on one row because no lot records which broker holds it. Net cost
// over net quantity is then 0.5825, a price nobody shorted at or paid — the
// short averages 0.5843 and the long 1.2000 — so the other side is returned
// apart, to be shown beside the figure and never averaged into it. A lot of
// no quantity still carrying cost stays on the row's side, so that on a row
// holding one side the figure times the quantity is the cost basis shown.
const perShare = rows => {
  const quantity = total(rows, field("quantity")), cost = total(rows, field("cost_basis"));
  return { quantity, cost_per_share: quantity && cost !== null ? cost / quantity : null };
};
const costPerShare = rows => {
  const net = total(rows, field("quantity"));
  if (!net) return { cost_per_share: null, opposite_side: null };
  const side = Math.sign(net), sideOf = r => Math.sign(number(r.quantity));
  const opposite = rows.filter(r => sideOf(r) === -side);
  return {
    cost_per_share: perShare(rows.filter(r => sideOf(r) !== -side)).cost_per_share,
    opposite_side: opposite.length ? perShare(opposite) : null,
  };
};

const aggregate = rows => {
  // A price and a valuation method describe the instrument, not the lot, so
  // one of them may never stand for the rest. §0.17: a computed label may not
  // name a condition the code did not test, and "Mark-to-Market" over a lot
  // carried at cost is that mistake. Prices that disagree show no price.
  const prices = distinct(rows, r => (number(r.last_price) === null ? null : String(number(r.last_price))));
  return {
    ...rows[0],
    lot_id: rows.length === 1 ? rows[0].lot_id : null,
    lot_ids: rows.map(r => r.lot_id).filter(v => v !== null && v !== undefined),
    lot_count: rows.length,
    quantity: total(rows, field("quantity")),
    cost_basis: total(rows, field("cost_basis")),
    market_value: total(rows, field("market_value")),
    mv_usd: total(rows, usdValue),
    unrealized_pnl: total(rows, field("unrealized_pnl")),
    accrued_interest: total(rows, field("accrued_interest")),
    face_value: total(rows, field("face_value")),
    ...costPerShare(rows),
    last_price: prices.length === 1 ? prices[0] : null,
    // The mark's own date travels with it. Lots of one security share a price,
    // so they share its date; a disagreement means these are not the same mark
    // and none of them is reported, exactly as the price itself is.
    price_date: distinct(rows, r => r.price_date).length === 1
      ? distinct(rows, r => r.price_date)[0] : null,
    price_source: distinct(rows, r => r.price_source).length === 1
      ? distinct(rows, r => r.price_source)[0] : null,
    price_age_days: distinct(rows, r => (r.price_age_days ?? null)).length === 1
      ? distinct(rows, r => (r.price_age_days ?? null))[0] : null,
    deal_name: distinct(rows, r => r.deal_name).join(", "),
    valuation_method: distinct(rows, r => r.valuation_method).join(" / "),
  };
};

/**
 * Roll a NAV payload's per-lot rows up into positions.
 *
 * `venueOf` reads where a row is held; it defaults to the venue the row itself
 * carries. Insertion order is kept, so a caller's own sort still decides what
 * the reader sees first.
 */
export function rollUpPositions(lots, venueOf = venueOnRow) {
  const grouped = new Map();
  for (const lot of lots || []) {
    const row = { ...lot, venue: venueOf(lot) };
    const key = groupKey(row);
    const existing = grouped.get(key);
    if (existing) existing.push(row); else grouped.set(key, [row]);
  }
  return [...grouped.values()].map(aggregate);
}
